import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import type {FinancialProductMoney} from '../schemas/FinancialProductMoneySchema';
import type {FinancialProductUnavailableReason} from '../schemas/FinancialProductUnavailableReasonSchema';
import type {FinancingCell} from '../schemas/FinancingCellSchema';
import type {FinancialProductQuote} from '../types/FinancialProductQuote';
import type {FinancialProductQuoteCoordinates} from '../types/FinancialProductQuoteCoordinates';
import type {FinancialProductQuoteOffer} from '../types/FinancialProductQuoteOffer';
import {calculateFinancingAmounts} from './calculateFinancingAmounts';

const PERCENT_BASE = 100;

const unavailable = (reason: FinancialProductUnavailableReason): FinancialProductQuote => ({
  availability: 'UNAVAILABLE',
  unavailableReason: reason,
  calculation: null,
});

const isNonNegativeInteger = (value: number) => Number.isInteger(value) && value >= 0;

const toHighestDownPaymentCell = (highest: FinancingCell | null, cell: FinancingCell) =>
  isNil(highest) || cell.downPaymentPercentage > highest.downPaymentPercentage ? cell : highest;

/**
 * The backend `FinancingOfferResolver` from step 4 on (steps 1–3 are why rates are withheld), in
 * its order and with its reasons. A down payment between brackets floors onto the highest bracket
 * at or below it for the same term and balloon, with no upper bound, and is priced at the
 * REQUESTED down payment with the floored cell's rates. Null when the coordinates are incomplete
 * or invalid, or the floored cell's rates are withheld.
 */
export function resolveFinancingQuote(
  vehiclePriceWithVat: FinancialProductMoney,
  offer: FinancialProductQuoteOffer,
  coordinates: FinancialProductQuoteCoordinates
): FinancialProductQuote | null {
  const termMonths = coordinates.termMonths;
  const downPaymentPercentage = coordinates.downPaymentPercentage;
  const balloonPaymentPercentage = coordinates.balloonPaymentPercentage ?? null;
  const downPaymentStep = offer.downPaymentStep;
  const cells = offer.availableFinancingCells;

  if (
    isNil(termMonths) ||
    isNil(downPaymentPercentage) ||
    isNil(cells) ||
    !Number.isInteger(termMonths) ||
    termMonths <= 0 ||
    !isNonNegativeInteger(downPaymentPercentage) ||
    downPaymentPercentage >= PERCENT_BASE ||
    (isNotNil(balloonPaymentPercentage) && !isNonNegativeInteger(balloonPaymentPercentage)) ||
    !Number.isInteger(downPaymentStep) ||
    downPaymentStep <= 0
  ) {
    return null;
  }

  const isBalloon = offer.productType === 'BALLOON';
  const bracketCells = cells.filter(
    (cell) =>
      cell.termMonths === termMonths && cell.balloonPaymentPercentage === balloonPaymentPercentage
  );
  const cell = bracketCells
    .filter((bracketCell) => bracketCell.downPaymentPercentage <= downPaymentPercentage)
    .reduce(toHighestDownPaymentCell, null);

  // The backend steps in order, the first match answers: 4. the term is not enabled; 5. the down
  // payment does not fit the configured step; 6. a balloon on a standard product, none on a balloon
  // product, or no cell for (term, balloon); 7. below the lowest bracket of (term, balloon). The
  // floored cell's rates being withheld answers null.
  return match({
    isTermEnabled: offer.availableTerms.includes(termMonths),
    fitsDownPaymentStep: downPaymentPercentage % downPaymentStep === 0,
    fitsBalloon:
      isBalloon === isNotNil(balloonPaymentPercentage) && (!isBalloon || bracketCells.length > 0),
    cell,
    nominalRate: cell?.nominalRate,
    apr: cell?.apr,
  })
    .returnType<FinancialProductQuote | null>()
    .with({isTermEnabled: false}, () => unavailable('UNSUPPORTED_TERM'))
    .with({fitsDownPaymentStep: false}, () => unavailable('UNSUPPORTED_DOWN_PAYMENT'))
    .with({fitsBalloon: false}, () => unavailable('UNSUPPORTED_BALLOON_PAYMENT'))
    .with({cell: Pattern.nullish}, () => unavailable('UNSUPPORTED_DOWN_PAYMENT'))
    .with(
      {cell: Pattern.nonNullable, nominalRate: Pattern.string, apr: Pattern.string},
      (priced) => {
        const calculation = calculateFinancingAmounts({
          vehiclePriceWithVat,
          processingFee: offer.processingFee ?? null,
          termMonths: priced.cell.termMonths,
          downPaymentPercentage,
          balloonPaymentPercentage: priced.cell.balloonPaymentPercentage,
          nominalRate: priced.nominalRate,
          apr: priced.apr,
        });

        // With validated inputs the calculator refuses only a balloon payment not below the net
        // credit; the backend composer maps that refusal per product.
        if (isNil(calculation)) {
          return unavailable(
            isBalloon ? 'UNSUPPORTED_BALLOON_PAYMENT' : 'UNSUPPORTED_DOWN_PAYMENT'
          );
        }

        return {availability: 'AVAILABLE', unavailableReason: null, calculation};
      }
    )
    .otherwise(() => null);
}
